import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { flattenNodes } from '@/lib/node-tree';
import { useAuthStore } from '@/stores/auth';
import { BehaviorDescriptionHint } from '@/components/BehaviorDescriptionHint';
import type { NodesResponse } from '@/stores/scope';

/**
 * 「人材像マトリクス」タブ (人・組織機能 増分② D-629ゲート#4、SC-01のマトリクス)。
 * 旧 `/talent/competency-matrix` (`CompetencyMatrixPage.tsx`) を人材育成ダッシュボードへ移設
 * (docs/design/v05-talent-development-dashboard-detail.md §0-2)。
 *
 * `GET /competency/matrix` はテナント全体で共通の定義データ (店舗差分を持たない) を返すが、
 * ゲート自体は「呼び出し元が最低1店舗の skill:view を持つか」の確認として node_id を要求する
 * (competency.ts のコメント参照)。画面の表示内容は node_id に依存しないため、
 * ノード選択 UI は出さない (§6d 過剰なUIを避ける)。
 *
 * ⚠️ QA/design-and-code 両レビューの HIGH/CRITICAL 指摘対策: `GET /nodes` はテナント全体の
 * 未フィルタなツリーを返す (permission でフィルタされていない) ため、単純に「先頭ノード」を使うと
 * own_store/node_scope スコープのユーザーの scope に含まれないノードを渡してしまい、
 * `isResourceInPermissionScope` が false になり毎回403のデッドエンドになる。
 * JWT の `scope_stores` (クライアントでは `user.stores`) はまさにこの own_store 判定が参照する
 * 配列そのものなので、まずそちらから選び、無ければ (all_stores 等 scope_stores が空でも許可される
 * ケース) テナント全体の先頭ノードにフォールバックする。self scope しか持たないユーザーは
 * navigation.ts 側で requiresNonSelfScope により画面自体を出さない (isResourceInPermissionScope が
 * self scope の分岐を持たないため、この画面に限らずどのノードを選んでも403になる)。
 *
 * v0.5 人材育成ダッシュボード §1-2: 移設にあわせ、選択中人材像の最低習熟度チップの隣に「?」アイコンで
 * 行動記述 (behavior_description、既に `/masters/skill-levels` が返す) を表示する。
 */

type BusinessFunction = { business_function_id: string; code: string; label: string; group_label: string | null; sort_order: number };
type MatrixSkill = {
  skill_id: string; code: string; label: string;
  business_function_id: string | null; abstraction_level: string | null; scale_id: string | null; is_enabled: boolean;
};
type RoleProfileRow = { role_profile_id: string; code: string; label: string; series: string | null; sort_order: number };
type RoleProfileSkill = { role_profile_id: string; skill_id: string; min_skill_level_id: string };
type RolePrerequisite = { role_profile_id: string; prerequisite_role_profile_id: string; logic_type: 'and' | 'or'; group_no: number };
type MatrixResponse = {
  business_functions: BusinessFunction[];
  skills: MatrixSkill[];
  role_profiles: RoleProfileRow[];
  role_profile_skills: RoleProfileSkill[];
  role_prerequisites: RolePrerequisite[];
};
type SkillLevelMaster = { skill_level_id: string; label: string; behavior_description: string | null };
// D-720 (2026-09-06): 抽象度は固定3値からテナント別マスタへ移行 (§13-3)。
type AbstractionLevel = { code: string; label: string; sort_order: number };

export function CompetencyMatrixTab() {
  const user = useAuthStore((s) => s.user);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [mobileBusinessFunctionId, setMobileBusinessFunctionId] = useState('');

  const { data: nodesData, isLoading: nodesLoading, isError: nodesError } = useQuery<NodesResponse>({
    queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes'),
  });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  // own_store/multi_store スコープのユーザーは user.stores (JWT scope_stores) に含まれるノードでないと
  // isResourceInPermissionScope に弾かれるため、まずそちらから選ぶ。all_stores 等で空の場合のみ
  // テナント全体の先頭ノードにフォールバックする (all_stores はどのノードでも許可されるため安全)。
  const nodeId = user?.stores?.[0] ?? flatNodes[0]?.node.node_id ?? '';

  const { data, isLoading, isError } = useQuery<MatrixResponse>({
    queryKey: ['competency-matrix', nodeId],
    queryFn: () => apiFetch<MatrixResponse>(`/competency/matrix?node_id=${encodeURIComponent(nodeId)}`),
    enabled: !!nodeId,
  });
  const { data: skillLevels = [] } = useQuery<SkillLevelMaster[]>({ queryKey: ['skill-levels'], queryFn: () => apiFetch('/masters/skill-levels') });
  const levelLabel = (id: string) => skillLevels.find((l) => l.skill_level_id === id)?.label ?? id;
  const levelBehaviorDescription = (id: string) => skillLevels.find((l) => l.skill_level_id === id)?.behavior_description ?? null;
  const { data: abstractionLevelsRaw = [] } = useQuery<AbstractionLevel[]>({ queryKey: ['abstraction-levels'], queryFn: () => apiFetch('/masters/abstraction-levels') });
  const abstractionLevels = [...abstractionLevelsRaw].sort((a, b) => a.sort_order - b.sort_order);

  const businessFunctions = [...(data?.business_functions ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const skills = data?.skills ?? [];
  const roleProfiles = [...(data?.role_profiles ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const roleProfileSkills = data?.role_profile_skills ?? [];
  const rolePrerequisites = data?.role_prerequisites ?? [];

  const requiredLevelBySkill = new Map(
    selectedRoleId
      ? roleProfileSkills.filter((r) => r.role_profile_id === selectedRoleId).map((r) => [r.skill_id, r.min_skill_level_id])
      : [],
  );
  const roleLabel = (id: string) => roleProfiles.find((r) => r.role_profile_id === id)?.label ?? id;

  const classifiedSkills = skills.filter((s) => s.business_function_id && s.abstraction_level);
  const unclassifiedSkills = skills.filter((s) => !s.business_function_id || !s.abstraction_level);

  const cellSkills = (businessFunctionId: string, level: string) =>
    classifiedSkills.filter((s) => s.business_function_id === businessFunctionId && s.abstraction_level === level);

  const renderSkillChip = (s: MatrixSkill) => {
    const requiredLevelId = requiredLevelBySkill.get(s.skill_id);
    const behaviorDescription = requiredLevelId ? levelBehaviorDescription(requiredLevelId) : null;
    return (
      <div key={s.skill_id} className={`text-xs rounded px-2 py-1 border ${
        requiredLevelId ? 'bg-success-bg text-success-text border-success-border' : 'bg-surface-sunken text-text-sub border-surface-border'
      }`}>
        <div className="flex items-center gap-1">
          {requiredLevelId && <CheckCircle2 size={12} aria-hidden className="shrink-0" />}
          <span className="break-words">{s.label}</span>
        </div>
        {requiredLevelId && (
          <div className="flex items-center gap-1 text-[11px] mt-0.5">
            <span>最低: {levelLabel(requiredLevelId)}以上</span>
            {behaviorDescription && <BehaviorDescriptionHint text={behaviorDescription} size={11} />}
          </div>
        )}
        {!s.is_enabled && <div className="text-[11px] mt-0.5">(無効)</div>}
      </div>
    );
  };

  const selectedPrerequisites = selectedRoleId
    ? rolePrerequisites.filter((p) => p.role_profile_id === selectedRoleId)
    : [];
  const prerequisiteGroups = new Map<number, RolePrerequisite[]>();
  for (const p of selectedPrerequisites) {
    const list = prerequisiteGroups.get(p.group_no) ?? [];
    list.push(p);
    prerequisiteGroups.set(p.group_no, list);
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-sub max-w-2xl">
        業務×抽象度でスキルを一覧し、人材像を選ぶとその人材像に必要なスキルをハイライトします (読み取り専用)。
      </p>

      {nodesLoading || (isLoading && !!nodeId) ? (
        <div role="status" className="text-text-sub">読み込み中…</div>
      ) : nodesError ? (
        <p role="alert" className="text-sm text-alert-text">組織情報の取得に失敗しました。</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-alert-text">マトリクスの取得に失敗しました。</p>
      ) : !nodeId ? (
        <EmptyState message="組織が見つかりません" description="組織階層が設定されていません。" />
      ) : businessFunctions.length === 0 && skills.length === 0 ? (
        <EmptyState message="業務・スキルがまだ登録されていません" description="「スキル・人材像」画面で先に業務・スキルを登録してください。" />
      ) : (
        <>
          <div className="max-w-xs">
            <label className="block text-xs text-text-sub mb-1" htmlFor="cm-role">人材像で絞り込み</label>
            <Select value={selectedRoleId || '__none__'} onValueChange={(v) => setSelectedRoleId(v === '__none__' ? '' : v)}>
              <SelectTrigger id="cm-role" aria-label="人材像で絞り込み"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">選択しない (全スキルを表示)</SelectItem>
                {roleProfiles.map((r) => <SelectItem key={r.role_profile_id} value={r.role_profile_id}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* ≥768px: 業務×抽象度のグリッド */}
          <div className="hidden md:block border border-surface-border rounded-lg overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-surface-sunken">
                    <th scope="col" className="relative px-3 py-2 text-left text-xs text-text-sub border-b border-r border-surface-border" style={{ minWidth: 140, maxWidth: 220 }}>業務</th>
                    {abstractionLevels.map((lv) => (
                      <th key={lv.code} scope="col" className="relative px-3 py-2 text-left text-xs text-text-sub border-b border-surface-border" style={{ minWidth: 180 }}>
                        {lv.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {businessFunctions.map((bf) => (
                    <tr key={bf.business_function_id} className="odd:bg-white even:bg-surface-stripe">
                      <th scope="row" className="px-3 py-2 text-left align-top border-r border-surface-border font-semibold text-text-body break-words">{bf.label}</th>
                      {abstractionLevels.map((lv) => (
                        <td key={lv.code} className="px-2 py-2 align-top">
                          <div className="flex flex-col gap-1">
                            {cellSkills(bf.business_function_id, lv.code).map(renderSkillChip)}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {businessFunctions.length === 0 && (
                    <tr><td colSpan={1 + abstractionLevels.length} className="px-3 py-6 text-center text-text-sub">業務が未登録です</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* <768px: 業務を選択してドリルダウン (D-647と同じパターン) */}
          <div className="md:hidden space-y-2">
            <div className="max-w-xs">
              <label className="block text-xs text-text-sub mb-1" htmlFor="cm-bf-mobile">業務で絞り込み</label>
              <Select value={mobileBusinessFunctionId || '__none__'} onValueChange={(v) => setMobileBusinessFunctionId(v === '__none__' ? '' : v)}>
                <SelectTrigger id="cm-bf-mobile" aria-label="業務で絞り込み"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">選択してください</SelectItem>
                  {businessFunctions.map((bf) => <SelectItem key={bf.business_function_id} value={bf.business_function_id}>{bf.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!mobileBusinessFunctionId ? (
              <p className="text-sm text-text-sub">上の「業務で絞り込み」からまず業務を選択してください。</p>
            ) : (
              <div className="space-y-2">
                {abstractionLevels.map((lv) => {
                  const cell = cellSkills(mobileBusinessFunctionId, lv.code);
                  if (cell.length === 0) return null;
                  return (
                    <div key={lv.code} className="border border-surface-border rounded-lg bg-white p-3">
                      <div className="text-xs text-text-sub mb-2">{lv.label}</div>
                      <div className="flex flex-col gap-1">{cell.map(renderSkillChip)}</div>
                    </div>
                  );
                })}
                {abstractionLevels.every((lv) => cellSkills(mobileBusinessFunctionId, lv.code).length === 0) && (
                  <EmptyState message="この業務に紐づくスキルがありません" />
                )}
              </div>
            )}
          </div>

          {unclassifiedSkills.length > 0 && (
            <div className="border border-surface-border rounded-lg bg-white p-3">
              <div className="text-xs text-text-sub mb-2">未分類 (業務または抽象度が未設定のスキル)</div>
              <div className="flex flex-wrap gap-1">{unclassifiedSkills.map(renderSkillChip)}</div>
            </div>
          )}

          {selectedRoleId && (
            <div className="border-t border-surface-line pt-4">
              <h2 className="text-sm font-semibold text-text-body">「{roleLabel(selectedRoleId)}」の前提役割</h2>
              {selectedPrerequisites.length === 0 ? (
                <p className="text-xs text-text-sub mt-1">前提役割はありません。</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {[...prerequisiteGroups.entries()].map(([groupNo, entries]) => (
                    <li key={groupNo} className="border border-surface-line rounded p-2">
                      <div className="text-[11px] text-text-sub mb-1">グループ {groupNo}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {entries.map((e) => (
                          <span key={e.prerequisite_role_profile_id} className="text-xs px-2 py-1 rounded bg-misespark-navy/10 text-misespark-navy whitespace-nowrap">
                            {roleLabel(e.prerequisite_role_profile_id)} ({e.logic_type.toUpperCase()})
                          </span>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
